import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios"
import { NormalizedErrorResult, normalizeApiErrorResponse } from "../api/error-result"

const BASE_API_URL = process.env.REACT_APP_API_URL || `http://localhost:8080`

export const makeApi = (axios: AxiosInstance) => {
  return {
    get: async <T>(url: string, cfg: AxiosRequestConfig = {}): Promise<T> => {
      try {
        const response = await axios.get(url, cfg)
        if (response.data?.success) return response.data.data as T
        throw response
      } catch (error) {
        return handleAxiosError(error)
      }
    },
    post: async <T>(url: string, body: any, cfg: AxiosRequestConfig = {}): Promise<T> => {
      try {
        const response = await axios.post(url, body, cfg)
        if (response.data?.success) return response.data.data as T
        throw response
      } catch (error) {
        return handleAxiosError(error)
      }
    },
    put: async <T>(url: string, body: any, cfg: AxiosRequestConfig = {}): Promise<T> => {
      try {
        const response = await axios.put(url, body, cfg)
        if (response.data?.success) return response.data.data as T
        throw response
      } catch (error) {
        return handleAxiosError(error)
      }
    },
    patch: async <T>(url: string, body: any, cfg: AxiosRequestConfig = {}): Promise<T> => {
      try {
        const response = await axios.patch(url, body, cfg)
        if (response.data?.success) return response.data.data as T
        throw response
      } catch (error) {
        return handleAxiosError(error)
      }
    },
    delete: async <T>(url: string, cfg: AxiosRequestConfig = {}): Promise<T> => {
      try {
        const response = await axios.delete(url, cfg)
        if (response.data?.success) return response.data.data as T
        if (response.status === 204) return {} as T
        throw response
      } catch (error) {
        return handleAxiosError(error)
      }
    },
  }
}

const isAxiosResponse = (error: unknown): error is AxiosResponse => {
  if (typeof error !== "object" || error === null) return false
  return "status" in error && "data" in error && "headers" in error && "config" in error
}

const handleAxiosError = (error: unknown): never => {
  if (isAxiosResponse(error)) throw normalizeApiErrorResponse(error.data)

  if (axios.isAxiosError(error)) {
    if (error.response) throw normalizeApiErrorResponse(error.response.data)
    if (error.request) throw NormalizedErrorResult.noResponse()
    throw NormalizedErrorResult.unknownError(error.message)
  }

  if (error instanceof Error) throw NormalizedErrorResult.unknownError(error.message)
  throw NormalizedErrorResult.unknownError()
}

export const axiosInstance = axios.create({ baseURL: BASE_API_URL })
export const api = makeApi(axiosInstance)
export default api
